import React from 'react';
import { Box, Button, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from 'ui/buttons/LoadingButton';
import { MuiModal } from 'ui/MuiModal';

export interface ConfirmDialogProps extends ConfirmDialogContentProps {
    open: boolean;
    noBackdropClick?: boolean;
}

export function ConfirmDialog({ open, noBackdropClick, ...rest }: ConfirmDialogProps) {
    return (
        <MuiModal open={open} onClose={noBackdropClick ? undefined : rest.onCancel}>
            <ConfirmDialogContent {...rest} />
        </MuiModal>
    );
}

interface ConfirmDialogContentProps {
    title: React.ReactNode;
    content: string;
    confirmLabel?: string;
    cancelLabel?: string;
    onCancel: () => void;
    loading?: boolean;
    showClose?: boolean;
    onClose?: () => void;
    onConfirm: () => void;
    confirmDisabled?: boolean;
    confirmDisabledTooltip?: string;
}

function ConfirmDialogContent({
    title,
    content,
    onCancel,
    loading,
    onConfirm,
    confirmLabel,
    cancelLabel,
    showClose = false,
    onClose,
    confirmDisabled = false,
    confirmDisabledTooltip
}: ConfirmDialogContentProps) {
    return (
        <React.Fragment>
            <Box display="flex" flexDirection="row" alignItems="center">
                <Box flexGrow={1}>
                    <Typography variant="h5" color="primary">
                        {title}
                    </Typography>
                </Box>
                {showClose && onClose && (
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                )}
            </Box>
            <Divider sx={{ my: 2, mx: 0 }} />
            <Typography sx={{ whiteSpace: 'pre-wrap' }} variant="body1">
                {content}
            </Typography>
            <Divider sx={{ my: 2, mx: 0 }} />
            <Box justifyContent="flex-end" sx={{ width: '100%', display: 'flex' }}>
                <Button sx={{ mr: 2.25 }} variant="outlined" color="primary" onClick={onCancel}>
                    {cancelLabel || 'Cancel'}
                </Button>
                <Tooltip title={confirmDisabled && confirmDisabledTooltip ? confirmDisabledTooltip : ''}>
                    <span>
                        <LoadingButton
                            loading={loading}
                            disabled={loading || confirmDisabled}
                            variant="contained"
                            color="primary"
                            onClick={onConfirm}
                        >
                            {confirmLabel || 'Confirm'}
                        </LoadingButton>
                    </span>
                </Tooltip>
            </Box>
        </React.Fragment>
    );
}
